"use client";

/**
 * Magic Link Login Page
 *
 * This page handles the magic link authentication flow:
 * 1. Extracts the token from URL query parameters
 * 2. Validates the token with the backend
 * 3. Creates a session if valid
 * 4. Redirects to the appropriate page based on backend response or query params
 *
 * Route: /[locale]/auth/magic-login?token=xxx&redirect=/path
 *
 * Architecture:
 * - Client component for handling browser-side authentication
 * - Uses Next-Auth for session management
 * - Separates business logic (magicLinkAuthService) from UI
 * - Provides clear loading and error states
 * - Type-safe with TypeScript
 */

import Image from "next/image";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import Button from "~/components/buttons/Button";
import ErrorBanner from "~/components/error-banner/ErrorBanner";
import Flex from "~/components/flex/Flex";
import Text from "~/components/texts/Text";
import { EMAIL_SERVICE } from "~/constants";
import { ROUTES_MAP } from "~/constants/routes";
import { useI18n } from "~/locales/client";
import { MagicLinkAuthState, MagicLinkError } from "~/types-and-enums/authTypes";
import { ButtonTypesEnum } from "~/types-and-enums/generalTypes";
import {
  authenticateWithMagicLink,
  canRetryAuth,
  extractRedirectUrl,
  extractTokenFromUrl,
  getErrorMessage,
} from "~/utils/magicLinkAuthService";

/**
 * Magic Link Login Page Component
 */
const MagicLoginPage = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = useParams();
  const translate = useI18n();

  const [authState, setAuthState] = useState<MagicLinkAuthState>(MagicLinkAuthState.IDLE);
  const [error, setError] = useState<MagicLinkError | null>(null);
  const redirectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const redirect = searchParams.get("redirect");

    // Persist redirect URL across re-renders
    if (redirect) redirectUrlRef.current = redirect;

    // Prevent duplicate authentication
    if (authState !== MagicLinkAuthState.IDLE) return;

    const authenticate = async () => {
      setAuthState(MagicLinkAuthState.VALIDATING);

      // Extract token from URL
      const token = extractTokenFromUrl(searchParams);

      if (!token) {
        // Redirect to login page when no token is present
        const locale = (params.locale as string) || "en";
        const redirect = searchParams.get("redirect");

        // Build login URL with redirect parameter if it exists
        let loginUrl = `/${locale}${ROUTES_MAP.authLogin}`;
        if (redirect) {
          const encodedRedirect = encodeURIComponent(redirect);
          loginUrl += `?redirect=${encodedRedirect}`;
        }

        // Use window.location for immediate redirect to prevent any UI flash
        window.location.href = loginUrl;
        return;
      }

      // Extract and sanitize redirect URL
      const redirectUrl = extractRedirectUrl(searchParams);

      // Call backend validation
      const result = await authenticateWithMagicLink({
        token,
        callbackUrl: redirectUrl,
        redirect: false, // We handle redirect manually
        translate,
      });

      if (result.success) {
        setAuthState(MagicLinkAuthState.SUCCESS);

        // Determine redirect destination
        const rawRedirect = result.redirectUrl || redirectUrl;
        // Get locale from route params, fallback to "en" if not available
        const locale = (params.locale as string) || "en";

        let finalRedirect = `/${locale}/stock-list`; // fallback

        if (rawRedirect) {
          if (/^https?:\/\//i.test(rawRedirect)) {
            // Absolute URL (e.g., full link from backend)
            window.location.href = rawRedirect;
            return;
          }

          // Normalize to relative path
          let cleaned = rawRedirect.startsWith("/") ? rawRedirect : `/${rawRedirect}`;
          if (!cleaned.startsWith(`/${locale}/`)) {
            cleaned = `/${locale}${cleaned}`;
          }
          finalRedirect = cleaned;
        }

        // Use window.location.href for full page reload to ensure cookies are available
        // This ensures the session cookie is properly read by the middleware
        setTimeout(() => {
          window.location.href = finalRedirect;
        }, 400);
      } else {
        // Check if link is expired or invalid - redirect to login page
        const errorState = result.error?.state;
        const errorMessage = result.error?.message || "";
        const locale = (params.locale as string) || "en";

        // Check for expired or invalid token (also check error message as fallback)
        const isExpiredOrInvalid =
          errorState === MagicLinkAuthState.EXPIRED_TOKEN ||
          errorState === MagicLinkAuthState.INVALID_TOKEN ||
          errorMessage.toLowerCase().includes("expired") ||
          errorMessage.toLowerCase().includes("invalid");

        if (isExpiredOrInvalid) {
          // Redirect to login page with error message immediately
          const encodedError = encodeURIComponent(result.error?.message || translate("auth.magicLink.error.expired"));
          // Use window.location for immediate redirect to prevent any UI flash
          window.location.href = `/${locale}${ROUTES_MAP.authLogin}?error=${encodedError}`;
          return;
        }

        // For other errors, show error state
        setAuthState(errorState || MagicLinkAuthState.ERROR);
        setError(result.error || null);
      }
    };

    authenticate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, router, translate]);

  /**
   * Handle retry action
   */
  const handleRetry = () => {
    setAuthState(MagicLinkAuthState.IDLE);
    setError(null);
    // Trigger re-authentication by changing state
    window.location.reload();
  };

  /**
   * Render loading state
   */
  if (authState === MagicLinkAuthState.VALIDATING || authState === MagicLinkAuthState.IDLE) {
    return (
      <Flex className="flex-col items-center justify-center min-h-screen gap-4">
        <Image src="/images/A_2a.gif" alt="" width={60} height={60} unoptimized />
        <p className="text-gray-600">{translate("auth.magicLink.verifying")}</p>
      </Flex>
    );
  }

  /**
   * Render success state
   */
  if (authState === MagicLinkAuthState.SUCCESS) {
    return (
      <Flex className="flex-col items-center justify-center min-h-screen gap-4">
        <Image src="/images/A_2a.gif" alt="" width={60} height={60} unoptimized />
        <p className="text-green-600 font-semibold">{translate("auth.magicLink.success")}</p>
        <p className="text-gray-600">{translate("auth.magicLink.redirecting")}</p>
      </Flex>
    );
  }

  /**
   * Render error state
   */
  return (
    <Flex className="flex-col items-center justify-center min-h-[50vh] p-4">
      <Flex className="flex-col gap-4 max-w-md w-full">
        <ErrorBanner>
          <Text className="text-center">{error?.message || getErrorMessage(authState, translate)}</Text>
        </ErrorBanner>

        <Flex className="flex-col gap-4">
          {canRetryAuth(authState) && (
            <Button
              buttonStyle={ButtonTypesEnum.primary}
              text={translate("auth.magicLink.tryAgain")}
              onClick={handleRetry}
            />
          )}
        </Flex>

        <Flex className="justify-center text-sm text-gray-600 w-full">
          <Text className="text-center">
            {translate("auth.magicLink.needHelp")}{" "}
            <a
              href={`mailto:${EMAIL_SERVICE}`}
              className="underline font-medium hover:underline-offset-2 hover:text-primary"
            >
              {EMAIL_SERVICE}
            </a>
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default MagicLoginPage;
